import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { UnifiedChatMessage, AdminColumn, ChatMessageStatus } from '@/mocks/adminLiveChat';
import { unifiedMessages, getColumnMessages, adminColumnLabels, adminColumnIcons } from '@/mocks/adminLiveChat';
import { LoadingSkeleton } from '@/components/base/LoadingSkeleton';
import { ErrorState } from '@/components/base/ErrorState';
import UnifiedFilters from './components/UnifiedFilters';
import UnifiedMessageCard from './components/UnifiedMessageCard';
import UnifiedMessageDetail from './components/UnifiedMessageDetail';
import HostSimpleView from './components/HostSimpleView';

export default function AdminLiveChatPage() {
  const [messages, setMessages] = useState<UnifiedChatMessage[]>(unifiedMessages);
  const [selectedMessage, setSelectedMessage] = useState<UnifiedChatMessage | null>(null);
  const [activeColumn, setActiveColumn] = useState<AdminColumn>('todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'admin' | 'host'>('admin');
  const [showToast, setShowToast] = useState<string | null>(null);

  const [pageState, setPageState] = useState<'loading' | 'ready' | 'error'>('loading');

  const loadData = useCallback(() => {
    setPageState('loading');
    setTimeout(() => {
      const shouldError = Math.random() < 0.08;
      setPageState(shouldError ? 'error' : 'ready');
    }, 700);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  /* ─── Column counts ─── */
  const adminColumns: AdminColumn[] = ['todos', 'cabina', 'live', 'concursos', 'favoritos', 'revision'];

  const columnCounts = useMemo(() => {
    return Object.fromEntries(
      adminColumns.map((col) => [col, getColumnMessages(messages, col).length])
    ) as Record<AdminColumn, number>;
  }, [messages]);

  /* ─── Filtered messages ─── */
  const filteredMessages = useMemo(() => {
    return getColumnMessages(messages, activeColumn).filter((m) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        m.senderName.toLowerCase().includes(q) ||
        m.content.toLowerCase().includes(q) ||
        (m.songRequest && m.songRequest.toLowerCase().includes(q)) ||
        (m.contestAnswer && m.contestAnswer.toLowerCase().includes(q)) ||
        m.sourceDetail.toLowerCase().includes(q)
      );
    });
  }, [messages, activeColumn, searchQuery]);

  /* ─── Stats ─── */
  const stats = useMemo(() => {
    const nuevos = messages.filter((m) => m.status === 'nuevo').length;
    const pendientes = messages.filter((m) => m.status === 'pendiente').length;
    const fijados = messages.filter((m) => m.status === 'fijado').length;
    const favoritos = messages.filter((m) => m.status === 'favorito').length;
    const alAire = messages.filter((m) => m.status === 'leido_al_aire').length;
    const bloqueados = messages.filter((m) => m.status === 'bloqueado').length;
    return { nuevos, pendientes, fijados, favoritos, alAire, bloqueados, total: messages.length };
  }, [messages]);

  const totalNew = stats.nuevos + stats.pendientes;

  /* ─── Toast helper ─── */
  const showToastMsg = (msg: string) => {
    setShowToast(msg);
    setTimeout(() => setShowToast(null), 2000);
  };

  /* ─── Handlers ─── */
  const handleSelectMessage = (msg: UnifiedChatMessage) => {
    setSelectedMessage(msg);
    if (msg.status === 'nuevo') {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msg.id ? { ...m, status: 'pendiente' as ChatMessageStatus } : m,
        ),
      );
    }
  };

  const handleToggleFavorite = (msg: UnifiedChatMessage) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msg.id
          ? {
              ...m,
              status:
                m.status === 'favorito'
                  ? ('pendiente' as ChatMessageStatus)
                  : ('favorito' as ChatMessageStatus),
            }
          : m,
      ),
    );
    showToastMsg(
      msg.status === 'favorito' ? 'Quitado de favoritos' : 'Marcado como favorito',
    );
  };

  const handleTogglePin = (msg: UnifiedChatMessage) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msg.id
          ? {
              ...m,
              status:
                m.status === 'fijado'
                  ? ('pendiente' as ChatMessageStatus)
                  : ('fijado' as ChatMessageStatus),
            }
          : m,
      ),
    );
    showToastMsg(msg.status === 'fijado' ? 'Desfijado' : 'Fijado arriba');
  };

  const handleMarkAir = (msg: UnifiedChatMessage) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msg.id ? { ...m, status: 'leido_al_aire' as ChatMessageStatus } : m,
      ),
    );
    showToastMsg('Leído al aire');
  };

  const handleMarkPending = (msg: UnifiedChatMessage) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msg.id ? { ...m, status: 'pendiente' as ChatMessageStatus } : m,
      ),
    );
    showToastMsg('Marcado como pendiente');
  };

  const handleBlock = (msg: UnifiedChatMessage) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msg.id
          ? {
              ...m,
              status:
                m.status === 'bloqueado'
                  ? ('pendiente' as ChatMessageStatus)
                  : ('bloqueado' as ChatMessageStatus),
            }
          : m,
      ),
    );
    if (selectedMessage?.id === msg.id) {
      setSelectedMessage((prev) =>
        prev ? { ...prev, status: msg.status === 'bloqueado' ? 'pendiente' : 'bloqueado' } : null,
      );
    }
    showToastMsg(msg.status === 'bloqueado' ? 'Desbloqueado' : 'Bloqueado');
  };

  const handleBanIP = (msg: UnifiedChatMessage) => {
    setMessages((prev) => prev.filter((m) => m.senderIP !== msg.senderIP));
    if (selectedMessage?.senderIP === msg.senderIP) {
      setSelectedMessage(null);
    }
    showToastMsg('IP ' + msg.senderIP + ' baneada — todos los mensajes de esta IP fueron eliminados');
  };

  const handleArchive = (msg: UnifiedChatMessage) => {
    setMessages((prev) => prev.filter((m) => m.id !== msg.id));
    if (selectedMessage?.id === msg.id) {
      setSelectedMessage(null);
    }
    showToastMsg('Archivado');
  };

  const handleHide = (msg: UnifiedChatMessage) => {
    setMessages((prev) => prev.filter((m) => m.id !== msg.id));
    if (selectedMessage?.id === msg.id) {
      setSelectedMessage(null);
    }
    showToastMsg('Ocultado');
  };

  const handleReply = (msg: UnifiedChatMessage) => {
    const replyText = `@${msg.senderName} `;
    showToastMsg('Escribí tu respuesta en la cabina para ' + msg.senderName);
    setSelectedMessage(msg);
  };

  /* ─── Mobile: selected message drawer ─── */
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);

  const handleMobileSelect = (msg: UnifiedChatMessage) => {
    setSelectedMessage(msg);
    setMobileDetailOpen(true);
    if (msg.status === 'nuevo') {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msg.id ? { ...m, status: 'pendiente' as ChatMessageStatus } : m,
        ),
      );
    }
  };

  return (
    <div className="min-h-screen bg-background-100">
      {/* Toast */}
      {showToast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold shadow-lg animate-pulse">
          {showToast}
        </div>
      )}

      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-secondary-500 flex items-center justify-center">
              <i className="ri-chat-3-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Chat Unificado</h1>
              <p className="text-foreground-400 text-[9px]">
                {totalNew > 0 ? (
                  <span className="text-primary-500 font-semibold">{totalNew} nuevos</span>
                ) : (
                  'Bandeja'
                )}
                {stats.total > 0 && ' · ' + stats.total + ' total'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setViewMode(viewMode === 'admin' ? 'host' : 'admin')}
              className={'px-2.5 py-1.5 rounded-lg text-[10px] font-semibold cursor-pointer transition-colors whitespace-nowrap ' + (
                viewMode === 'host'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-background-100 text-foreground-500'
              )}
            >
              <i className={(viewMode === 'host' ? 'ri-admin-line' : 'ri-megaphone-line') + ' text-[10px] mr-0.5'}></i>
              {viewMode === 'host' ? 'Admin' : 'Locutor'}
            </button>
            <Link
              to="/"
              className="flex items-center gap-1 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
            >
              <i className="ri-arrow-left-line text-xs"></i>
            </Link>
          </div>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0 flex flex-col">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div className="flex items-center gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">
                  Chat Unificado — {viewMode === 'admin' ? 'Vista Administrador' : 'Vista Locutor'}
                </h2>
                <p className="text-foreground-400 text-xs">
                  {stats.total} mensajes · {totalNew} nuevos · {stats.fijados} fijados · {stats.favoritos} favoritos
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <Link
                  to="/admin/cabina"
                  className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-secondary-600 bg-secondary-50 hover:bg-secondary-100 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-broadcast-line text-xs mr-1"></i>
                  Cabina
                </Link>
                <Link
                  to="/admin/cabina/queue"
                  className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-foreground-500 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-list-check text-xs mr-1"></i>
                  Queue
                </Link>
                <Link
                  to="/admin/contests"
                  className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-foreground-400 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-trophy-line text-xs mr-1"></i>
                  Concursos
                </Link>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center bg-background-100 rounded-full p-0.5">
                <button
                  onClick={() => setViewMode('admin')}
                  className={'px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap ' + (
                    viewMode === 'admin'
                      ? 'bg-background-50 text-foreground-950 shadow-sm'
                      : 'text-foreground-400 hover:text-foreground-600'
                  )}
                >
                  <i className="ri-layout-grid-line text-[11px] mr-1"></i>
                  Admin
                </button>
                <button
                  onClick={() => setViewMode('host')}
                  className={'px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap ' + (
                    viewMode === 'host'
                      ? 'bg-background-50 text-foreground-950 shadow-sm'
                      : 'text-foreground-400 hover:text-foreground-600'
                  )}
                >
                  <i className="ri-megaphone-line text-[11px] mr-1"></i>
                  Locutor
                </button>
              </div>
              <Link
                to="/"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line text-xs"></i>
                Ver sitio
              </Link>
            </div>
          </div>

          {/* ADMIN VIEW */}
          {viewMode === 'admin' && (
            <div className="flex-1 flex flex-col lg:flex-row min-h-0">
              {pageState === 'loading' && (
                <div className="flex-1 flex items-start justify-center py-10">
                  <LoadingSkeleton variant="list" rows={10} className="w-full max-w-3xl px-4" />
                </div>
              )}
              {pageState === 'error' && (
                <div className="flex-1 flex items-center justify-center">
                  <ErrorState
                    title="Error al cargar el chat"
                    message="No pudimos obtener los mensajes del chat unificado. Intentá de nuevo."
                    onRetry={loadData}
                  />
                </div>
              )}
              {pageState === 'ready' && (
                <>
                  <div className="flex-1 lg:flex-[0.4] xl:flex-[0.35] min-h-0 border-b lg:border-b-0 lg:border-r border-background-200 flex flex-col">
                    <UnifiedFilters
                      mode="admin"
                      activeColumn={activeColumn}
                      onChangeColumn={setActiveColumn}
                      searchQuery={searchQuery}
                      onSearchChange={setSearchQuery}
                      columnCounts={columnCounts}
                      totalNew={totalNew}
                    />
                    <div className="flex-1 overflow-y-auto">
                      {filteredMessages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                          <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-3">
                            <i className="ri-inbox-line text-foreground-300 text-xl"></i>
                          </div>
                          <p className="text-sm font-bold text-foreground-400">Sin mensajes</p>
                          <p className="text-[10px] text-foreground-400 mt-0.5">No hay mensajes en esta columna</p>
                        </div>
                      ) : (
                        filteredMessages.map((msg) => (
                          <UnifiedMessageCard
                            key={msg.id}
                            message={msg}
                            isSelected={selectedMessage?.id === msg.id}
                            isHostView={false}
                            onSelect={handleMobileSelect}
                            onToggleFavorite={handleToggleFavorite}
                            onTogglePin={handleTogglePin}
                            onMarkAir={handleMarkAir}
                            onArchive={handleArchive}
                          />
                        ))
                      )}
                    </div>
                  </div>
                  <div className="hidden lg:flex flex-1 lg:flex-[0.6] xl:flex-[0.65] min-h-0">
                    <UnifiedMessageDetail
                      message={selectedMessage}
                      onReply={handleReply}
                      onToggleFavorite={handleToggleFavorite}
                      onTogglePin={handleTogglePin}
                      onMarkAir={handleMarkAir}
                      onMarkPending={handleMarkPending}
                      onBlock={handleBlock}
                      onBanIP={handleBanIP}
                      onArchive={handleArchive}
                      onHide={handleHide}
                    />
                  </div>
                  {mobileDetailOpen && selectedMessage && (
                    <div className="lg:hidden fixed inset-0 z-50 flex flex-col justify-end">
                      <div className="absolute inset-0 bg-black/40" onClick={() => setMobileDetailOpen(false)}></div>
                      <div className="relative bg-background-50 rounded-t-2xl max-h-[80vh] flex flex-col animate-slide-up">
                        <div className="w-10 h-1 rounded-full bg-foreground-300 mx-auto my-2"></div>
                        <div className="flex-1 overflow-y-auto">
                          <UnifiedMessageDetail
                            message={selectedMessage}
                            onReply={handleReply}
                            onToggleFavorite={handleToggleFavorite}
                            onTogglePin={handleTogglePin}
                            onMarkAir={handleMarkAir}
                            onMarkPending={handleMarkPending}
                            onBlock={handleBlock}
                            onBanIP={handleBanIP}
                            onArchive={handleArchive}
                            onHide={handleHide}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* HOST VIEW */}
          {viewMode === 'host' && (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 flex min-h-0">
                <HostSimpleView
                  messages={messages}
                  selectedMessage={selectedMessage}
                  onSelect={handleMobileSelect}
                  onToggleFavorite={handleToggleFavorite}
                  onTogglePin={handleTogglePin}
                  onMarkAir={handleMarkAir}
                  onMarkPending={handleMarkPending}
                  onBlock={handleBlock}
                  onBanIP={handleBanIP}
                  onArchive={handleArchive}
                />
              </div>
            </div>
          )}
        </main>
      </div>


    </div>
  );
}